export const API = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.scai.fm/v1';

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export async function api<T>(path: string, init: RequestInit & { token?: string; deviceKey?: string } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.token) headers.set('X-Zone-Token', init.token);
  if (init.deviceKey) headers.set('X-Device-Key', init.deviceKey);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const r = await fetch(API + path, { ...init, headers });
  if (!r.ok) {
    let msg = r.statusText;
    try { msg = (await r.json()).detail ?? msg; } catch { /* not json */ }
    throw new ApiError(r.status, typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return r.json() as Promise<T>;
}

export type Branding = {
  zone: { id: string; name: string; volume_ceiling: number };
  customer: { name: string };
  branding: { title: string; logo: string | null; lamp_accent: string | null };
  hls_url: string;
  icecast_url: string;
};

export type NowItem = { kind: 'track' | 'silence' | 'announcement'; started_at: string; duration_ms: number;
  track: { id: string; title: string; artist: string; duration_ms: number } | null } | null;

export type NowPlaying = {
  zone: { id: string; name: string; status: 'active' | 'paused' | 'disabled'; volume_ceiling: number; playlist_kind?: 'auto' | 'manual' | null };
  follows: { id: string; name: string } | null;
  suspended?: boolean; // the customer's service is suspended: silence
  schedule: { always_open: boolean; open: boolean; daypart: string | null; next_change_at: string | null; hours_today: string[] };
  now: NowItem; next: NowItem; server_time: string;
};

export type DeviceCreds = { device_id: string; device_key: string; name: string; zone_id: string };

const KEY = 'scaifm.device';

export function loadDevice(): DeviceCreds | null {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
}

export function saveDevice(d: DeviceCreds | null) {
  try { d ? localStorage.setItem(KEY, JSON.stringify(d)) : localStorage.removeItem(KEY); } catch { /* storage off */ }
}

// --------------------------------------------------------------------------- public radio (no account)

/** What plays around now on a station; the player picks what its listener hears from the start times. */
export type RadioItem = { kind: 'track' | 'link' | 'clip'; started_at: string; duration_ms: number;
  track: { id: string; title: string; artist: string } | null; text: string | null; link_id: string | null };

export type RadioStation = {
  slug: string; name: string; description: string | null; preset: number | null; timezone: string; on_air: boolean;
  listeners: number; shouts: boolean;
  dj: { name: string; bio: string | null; talks: boolean } | null; // null: no host (a Studio station)
  programme: { slug: string; name: string; description: string | null; until: string | null };
  items: RadioItem[];
  stream: { hls: string; mp3: string };
};

export type RadioSchedule = { timezone: string; base: string; programmes: { slug: string; name: string; description: string | null }[];
  slots: { weekday: number; start: string; end: string; programme: string }[] };

export type PlaylistPage = { items: { played_at: string; track: { id: string; title: string; artist: string; duration_ms: number } }[]; before: string | null };
